import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useAccount } from 'wagmi';
import { ConnectButton } from '@rainbow-me/rainbowkit';
import { Unlock, Lock } from 'lucide-react';
import UnlockedConfessionsModal from './UnlockedTestimoniesModal';
import HowItWorksModal from './HowItWorksModal';
import WalletModal from './WalletModal';
import './Header.css';

const Header = ({ confessionGalleryRef, isHidden = false, unlockedCount = 0 }) => {
  const [showUnlockedModal, setShowUnlockedModal] = useState(false);
  const [showHowItWorksModal, setShowHowItWorksModal] = useState(false);
  const [showWalletModal, setShowWalletModal] = useState(false);
  const { address, isConnected } = useAccount();

  // Check if we're in miniapp mode
  const isMiniApp = window?.parent !== window || 
                    navigator.userAgent.includes('MiniApp') ||
                    window.location.search.includes('miniapp=true');


  return (
    <>
      <motion.header 
        className="header glass"
        initial={{ y: -100, opacity: 0 }}
        animate={{ 
          y: isHidden ? -100 : 0, 
          opacity: isHidden ? 0 : 1 
        }}
        transition={{ duration: 0.6 }}
      >
        <div className="container">
          <div className="header-content">
            <motion.div 
              className="logo"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <img src="/fejlec-logo.png" alt="GuiltBox - Confession, Coins, Charity" />
              <span>Confession • Coins • Charity</span>
            </motion.div>
            
            <nav className="nav">
              <motion.a 
                href="#confessions" 
                className="nav-link"
                whileHover={{ y: -2 }}
              >
                Gallery
              </motion.a>
              <motion.a 
                href="#submit" 
                className="nav-link"
                whileHover={{ y: -2 }}
              >
                Submit
              </motion.a>
              <motion.a
                href="#how-it-works"
                className="nav-link"
                onClick={(e) => {
                  e.preventDefault();
                  setShowHowItWorksModal(true);
                }}
                whileHover={{ y: -2 }}
              >
                How it works
              </motion.a>
            </nav>

            <div className="wallet-section">
              <div className="wallet-actions">
                {isConnected && (
                  <motion.button
                    className="btn btn-unlock"
                    onClick={() => {
                      // Scroll to gallery and switch to unlocked tab
                      document.getElementById('confessions')?.scrollIntoView({ behavior: 'smooth' });
                      setTimeout(() => {
                        confessionGalleryRef?.current?.switchToUnlockedTab();
                      }, 500);
                    }}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Unlock size={16} />
                    Unlocked Confessions
                  </motion.button>
                )}
                
                <ConnectButton.Custom>
                  {({
                    account,
                    chain,
                    openConnectModal,
                    openAccountModal,
                    mounted,
                  }) => {
                    // Loading state
                    const ready = mounted;
                    const connected = ready && account && chain;

                    return (
                      <div
                        {...(!ready && {
                          'aria-hidden': true,
                          'style': {
                            opacity: 0,
                            pointerEvents: 'none',
                            userSelect: 'none',
                          },
                        })}
                      >
                        {(() => {
                          if (!connected) {
                            return (
                              <motion.button
                                onClick={openConnectModal}
                                className="btn btn-primary connect-wallet-btn"
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                              >
                                Connect Wallet
                              </motion.button>
                            );
                          }

                          return (
                            <motion.button
                              onClick={() => setShowWalletModal(true)}
                              className="btn btn-secondary wallet-info-btn"
                              whileHover={{ scale: 1.02 }}
                              whileTap={{ scale: 0.98 }}
                            >
                              <div className="wallet-display">
                                <span className="wallet-address">
                                  {account.displayName}
                                </span>
                                <span className="unlocked-count">
                                  {unlockedCount} unlocked
                                </span>
                              </div>
                            </motion.button>
                          );
                        })()}
                      </div>
                    );
                  }}
                </ConnectButton.Custom>
              </div>
              
              {!isConnected && (
                <div className="no-wallet-info">
                  <Lock size={16} />
                  <span>Connect wallet to unlock confessions</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </motion.header>


      {/* Modals */}
      <UnlockedConfessionsModal 
        isOpen={showUnlockedModal} 
        onClose={() => setShowUnlockedModal(false)} 
      />
      <HowItWorksModal 
        isOpen={showHowItWorksModal} 
        onClose={() => setShowHowItWorksModal(false)} 
      />
      <WalletModal 
        isOpen={showWalletModal} 
        onClose={() => setShowWalletModal(false)}
        unlockedCount={unlockedCount}
      />
    </>
  );
};

export default Header;